import { useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import { IGeneralLedgerNode } from "src/pages/reports/general-ledger/table";
import apiClient from "../apiClient";

interface IParams {
  start_date: string;
  end_date: string;
  branches?: number[];
  enabled?: boolean;
}

interface ITrialBalanceAccount {
  id: number;
  code: string;
  name: string;
  node_type?: string;
  debit: number;
  credit: number;
  children?: ITrialBalanceAccount[];
}

interface ITrialBalanceReportResponse {
  data: ITrialBalanceAccount[];
}

interface IGeneralLedgerReportResponse {
  data: IGeneralLedgerNode[];
}

interface IBalanceSheetReportResponse {
  data: any;
}

export const useGetTrialBalanceReportData = (params: IParams) =>
  useQuery<ITrialBalanceReportResponse, APIErrorResponse, ITrialBalanceReportResponse>({
    queryKey: [
      "get-new-trial-balance-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id)),
      );
      return apiClient.get(
        `/api/reports/trial-balance?${searchParams.toString()}`,
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

export const useGetGeneralLedgerReportData = (params: IParams) =>
  useQuery<IGeneralLedgerReportResponse, APIErrorResponse, IGeneralLedgerReportResponse>({
    queryKey: [
      "get-general-ledger-report-data",
      params.start_date,
      params.end_date,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("start_date", params.start_date);
      searchParams.set("end_date", params.end_date);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id)),
      );
      return apiClient.get(
        `/api/reports/general-ledger?${searchParams.toString()}`,
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });

interface ISheetParams {
  date: string;
  period: string;
  branches?: number[];
  enabled?: boolean;
}
export const useGetBalanceSheetReportData = (params: ISheetParams) =>
  useQuery<IBalanceSheetReportResponse, APIErrorResponse, IBalanceSheetReportResponse>({
    queryKey: [
      "get-new-balance-sheet-report-data",
      params.date,
      params.period,
      (params.branches ?? []).join(","),
      params.enabled ?? true,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("date", params.date);
      searchParams.set("period", params.period);
      (params.branches ?? []).forEach((id) =>
        searchParams.append("branches[]", String(id)),
      );
      return apiClient.get(
        `/api/reports/balance-sheet?${searchParams.toString()}`,
      );
    },
    enabled: params.enabled !== false,
    gcTime: 0,
  });
